import { SettingsPermissions } from "@redotech/account-common/permissions";
import { directMerchantAccess } from "../../settings-catalog/access";
import type {
  SettingAgentEditability,
  SettingAvailability,
  SettingGroupDefinition,
  SettingOwner,
  SettingRisk,
} from "../../settings-catalog/types";

export type CustomerWidgetFlatTeamSettingGroupId =
  | "customer_widget.setup"
  | "customer_widget.appearance"
  | "customer_widget.self_serve"
  | "customer_widget.ai_product_form"
  | "customer_widget.accounts";

const customerWidgetSettingsOwner: SettingOwner = {
  kind: "teamSettings",
  path: "settings.customerWidget",
  product: "customer-widget",
};

const mediumCustomerFacingRisk: SettingRisk = {
  level: "medium",
  reason: "Changes can affect customer-visible storefront widget experiences.",
};

const requiresApproval: SettingAgentEditability = { mode: "requiresApproval" };
const requiresAiAvailableAvailability = {
  conditions: [{ type: "evaluator", evaluator: "team.canUseAi" }],
  unavailableBehavior: "hidden",
  reason:
    "AI sales agent widget settings are only visible when AI is available for the team.",
} as const satisfies SettingAvailability;
const requiresCustomerWidgetNavigationAvailability = {
  conditions: [
    { type: "evaluator", evaluator: "customerWidget.navigationAvailable" },
  ],
  unavailableBehavior: "hidden",
  reason:
    "Customer widget settings are only visible when Support, AI, or Customer Accounts is available.",
} as const satisfies SettingAvailability;
const requiresCustomerAccountsAvailability = {
  conditions: [{ type: "evaluator", evaluator: "customerAccounts.enabled" }],
  unavailableBehavior: "hidden",
  reason:
    "Customer account widget settings are only visible when Customer Accounts is enabled.",
} as const satisfies SettingAvailability;

export const CustomerWidgetFlatTeamSettingGroups = [
  {
    id: "customer_widget.setup",
    title: "Customer Widget Setup",
    description:
      "Storefront customer widget installation, embed visibility, and launcher visibility.",
    productArea: "customer-widget",
    access: directMerchantAccess(SettingsPermissions.SET_GENERAL_SETTINGS),
    owner: customerWidgetSettingsOwner,
    risk: mediumCustomerFacingRisk,
    agentEditability: requiresApproval,
    availability: requiresCustomerWidgetNavigationAvailability,
    fullPageTarget: { type: "settings", path: "customer-widget?tab=general" },
    synonyms: [
      "customer widget setup",
      "install customer widget",
      "enable customer widget",
      "hide widget button",
      "widget launcher visibility",
      "show customer widget",
    ],
    changeGuidance: {
      summary:
        "Confirm before changing whether the storefront customer widget is shown or hidden.",
    },
  },
  {
    id: "customer_widget.appearance",
    title: "Customer Widget Appearance",
    description:
      "Storefront customer widget branding, colors, launcher placement, text, logo, and sizing.",
    productArea: "customer-widget",
    access: directMerchantAccess(SettingsPermissions.SET_GENERAL_SETTINGS),
    owner: customerWidgetSettingsOwner,
    risk: mediumCustomerFacingRisk,
    agentEditability: requiresApproval,
    availability: requiresCustomerWidgetNavigationAvailability,
    fullPageTarget: { type: "settings", path: "customer-widget?tab=general" },
    synonyms: [
      "storefront customer widget",
      "chat widget appearance",
      "chat widget colors",
      "colors chat widget",
      "change chat widget colors",
      "support widget appearance",
      "widget branding",
      "widget colors",
      "launcher placement",
    ],
    changeGuidance: {
      summary:
        "Use focused setting ids for specific appearance changes such as colors, placement, text, logo, font size, advanced positioning, or CSS.",
    },
  },
  {
    id: "customer_widget.self_serve",
    title: "Customer Widget Self-Serve",
    description:
      "Self-serve actions customers can take from the customer widget, including returns, claims, order editing, and package tracking.",
    productArea: "customer-widget",
    access: directMerchantAccess(SettingsPermissions.SET_GENERAL_SETTINGS),
    owner: customerWidgetSettingsOwner,
    risk: mediumCustomerFacingRisk,
    agentEditability: requiresApproval,
    availability: requiresCustomerWidgetNavigationAvailability,
    fullPageTarget: { type: "settings", path: "customer-widget?tab=general" },
    synonyms: [
      "self serve settings",
      "self-service widget actions",
      "start return button",
      "start claim button",
      "edit order button",
      "track package button",
      "order actions",
    ],
    changeGuidance: {
      summary:
        "Confirm which customer-facing actions and labels should appear in the widget.",
    },
  },
  {
    id: "customer_widget.ai_product_form",
    title: "AI Product Form",
    description:
      "AI product-page question form placement, suggested-question count, copy, button colors, styling, and custom CSS.",
    productArea: "customer-widget",
    access: directMerchantAccess(SettingsPermissions.SET_GENERAL_SETTINGS),
    owner: {
      kind: "teamSettings",
      path: "settings.concierge",
      product: "customer-widget",
    },
    risk: mediumCustomerFacingRisk,
    agentEditability: requiresApproval,
    availability: requiresAiAvailableAvailability,
    fullPageTarget: { type: "settings", path: "customer-widget?tab=general" },
    synonyms: [
      "AI product form",
      "product page AI form",
      "product question form",
      "ask AI form",
      "AI question prompt",
      "suggested questions",
      "product form placement",
    ],
    changeGuidance: {
      summary:
        "Confirm placement and customer-facing copy before changing the product-page AI form.",
    },
  },
  {
    id: "customer_widget.accounts",
    title: "Customer Widget Accounts",
    description:
      "Customer account prompts, account page visibility, email sign-up incentives, and account tab modules.",
    productArea: "customer-widget",
    access: directMerchantAccess(
      SettingsPermissions.SET_CUSTOMER_ACCOUNTS_SETTINGS,
    ),
    owner: customerWidgetSettingsOwner,
    risk: mediumCustomerFacingRisk,
    agentEditability: requiresApproval,
    availability: requiresCustomerAccountsAvailability,
    fullPageTarget: { type: "settings", path: "customer-widget?tab=accounts" },
    synonyms: [
      "customer accounts",
      "accounts tab",
      "account modules",
      "sign in prompt",
      "email signup incentive",
      "sign up discount",
    ],
    changeGuidance: {
      summary:
        "Confirm customer-facing account copy, incentive amounts, and visible account modules before changing them.",
    },
  },
] as const satisfies readonly SettingGroupDefinition<CustomerWidgetFlatTeamSettingGroupId>[];

type _assertAllCustomerWidgetFlatTeamSettingGroupIdsProvided =
  CustomerWidgetFlatTeamSettingGroupId extends (typeof CustomerWidgetFlatTeamSettingGroups)[number]["id"]
    ? true
    : never;
const _allCustomerWidgetFlatTeamSettingGroupIdsProvided: _assertAllCustomerWidgetFlatTeamSettingGroupIdsProvided = true;
void _allCustomerWidgetFlatTeamSettingGroupIdsProvided;

export const CustomerWidgetFlatTeamSettingGroupById: Record<
  CustomerWidgetFlatTeamSettingGroupId,
  SettingGroupDefinition<CustomerWidgetFlatTeamSettingGroupId>
> = (() => {
  const map: Partial<
    Record<
      CustomerWidgetFlatTeamSettingGroupId,
      SettingGroupDefinition<CustomerWidgetFlatTeamSettingGroupId>
    >
  > = {};
  for (const group of CustomerWidgetFlatTeamSettingGroups) {
    map[group.id] = group;
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return map as Record<
    CustomerWidgetFlatTeamSettingGroupId,
    SettingGroupDefinition<CustomerWidgetFlatTeamSettingGroupId>
  >;
})();
